import { LangLocale } from '@myre/common-i18n';
import {
  DateFnsAdapter,
  LANGLOCALE,
  TranslatePipe,
} from '@myre/ui-primitive-core';

import { NgClass } from '@angular/common';
import {
  Component,
  forwardRef,
  inject,
  Input,
  ViewChild,
  ViewEncapsulation,
  type OnInit,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  type MatDateFormats,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';

import { type DateTriggerIcon } from '../../questions/question-date';

export const YEAR_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => YearPickerComponent),
  multi: true,
};

export const YEAR_PICKER_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'yyyy',
  },
  display: {
    dateInput: 'y',
    monthYearLabel: 'MMMM y',
    dateA11yLabel: 'y',
    monthYearA11yLabel: 'MMMM y',
  },
};

@Component({
  selector: 'myre-year-picker',
  templateUrl: './year-picker.component.html',
  styleUrls: ['./year-picker.component.scss'],
  providers: [
    YEAR_PICKER_VALUE_ACCESSOR,
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: YEAR_PICKER_FORMATS,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatInput,
    MatDatepickerInput,
    FormsModule,
    NgClass,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class YearPickerComponent implements OnInit, ControlValueAccessor {
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);
  private _dateAdapter = inject<DateAdapter<any>>(DateAdapter);

  @Input()
  public id: string;

  @Input()
  public required: boolean = false;

  @Input()
  public min: Date;

  @Input()
  public max: Date;

  @Input()
  public dateFilter: (date: Date) => boolean;

  @Input()
  public startAt: Date;

  @Input()
  public placeholder: string;

  @Input()
  public readonly: boolean = false;

  @Input()
  public error: boolean = false;

  @Input()
  public warning: boolean = false;

  @Input()
  public triggerIcon: DateTriggerIcon = 'myre-icon-calendar';

  @ViewChild(MatDatepicker, { static: false })
  public matDatepicker: MatDatepicker<any>;

  public value: Date;
  public disabled: boolean = false;
  public focus: boolean = false;

  public ngOnInit(): void {
    this._dateAdapter.setLocale(this._langLocale);
  }

  public writeValue(value: Date): void {
    this.value = value;
  }

  public registerOnChange(fn: () => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  public onChange(value: Date): void {
    this.value = value;
    this._onModelChange(value);
  }

  public onBlur(event?: FocusEvent, icon?: any): void {
    if (event && event.relatedTarget && event.relatedTarget === icon) {
      return;
    }

    this.focus = false;
    this._onTouched();
  }

  public onFocus(): void {
    this.focus = true;
  }

  public onIconClick(picker: MatDatepicker<Date>): void {
    picker.open();
  }

  public yearSelected(value: Date): void {
    this.onChange(value);
    this.matDatepicker.close();
  }

  private _onModelChange: (value: Date) => any = () => {};

  private _onTouched: () => any = () => {};
}
